import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ChevronLeft from "src/core/assets/images/icons/chevronLeft.svg?react";
import { DatePicker } from "src/core/components/custom-ui/date-picker";
import { FormCheckboxMultiple } from "src/core/components/custom-ui/form-checkbox-multiple";
import { FormCombobox } from "src/core/components/custom-ui/form-combobox";
import { FormInput } from "src/core/components/custom-ui/form-input";
import { FormSelect } from "src/core/components/custom-ui/form-select";
import { Button } from "src/core/components/ui/button";
import { MappedStakeholder } from "src/core/constants/folder";
import { ISelectOption } from "src/core/constants/options";
import { useRiskOptions } from "src/core/contexts/RiskOptionsContext";
import { useCountryOptions } from "src/core/hooks/common/useCountryOptions";
import { useUpdateStakeholderFullForm } from "src/core/hooks/folder/useUpdateStakeholderFullForm";
import { getGenderOptions } from "src/core/lib/options/getGenderOptions";
import { getStakeholderRoleOptions } from "src/core/lib/options/getStakeholderRoleOptions";
import { cn } from "src/core/lib/utils";
import { withRequiredOverride } from "src/core/overrides";

const DocumentsUploadFieldsetOverride = withRequiredOverride(
  "components.folder.DocumentsUploadFieldset"
);

type UpdateStakeholderFullFormProps = {
  /** Stakeholder à éditer. */
  selectedStakeholderForEdit: MappedStakeholder;

  /** Définit le stakeholder sélectionné. Passer `undefined` pour revenir à la liste. */
  setSelectedStakeholderForEdit?: (stakeholder?: MappedStakeholder) => void;

  /** Callback appelé lorsque le formulaire est soumis avec succès. Permet de déclencher un refetch de la liste des stakeholders. */
  onSuccess?: () => void;

  /** Définit si le bouton de retour doit être affiché. */
  inModal?: boolean;

  /** Définit si le formulaire doit être ouvert dans un modal. */
  setOpen?: (open: boolean) => void;
};

/**
 * Composant pour ajouter une partie prenante (stakeholder) à un dossier.
 * Formulaire complet avec toutes les informations nécessaires.
 */
export function UpdateStakeholderFullForm({
  selectedStakeholderForEdit,
  setSelectedStakeholderForEdit,
  onSuccess,
  inModal = false,
  setOpen,
}: UpdateStakeholderFullFormProps) {
  const { t } = useTranslation();

  const genderOptions = getGenderOptions(t);
  const stakeholderRoleOptions = getStakeholderRoleOptions(t);
  const {
    countryOptions,
    loading: countryLoading,
  }: { countryOptions: ISelectOption[]; loading: boolean } = useCountryOptions();

  const { countryWithRiskOptions } = useRiskOptions();

  const { form, onSubmit, isLoading, requiredDocuments, isReadOnly, incompleteFields } =
    useUpdateStakeholderFullForm({
      selectedStakeholderForEdit,
      setSelectedStakeholderForEdit: setSelectedStakeholderForEdit ?? (() => undefined),
      onSuccess,
    });

  return (
    <div className={!inModal ? "folder-stakeholders-form-content" : ""}>
      {!inModal && (
        <Button
          className="mb-4"
          variant="link"
          onClick={() => setSelectedStakeholderForEdit?.(undefined)}
        >
          <ChevronLeft />
          {t("stakeholders.list.backToList")}
        </Button>
      )}

      <div className="folder-stakeholders-data">
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="section-title">
              {t("folder.stakeholders.documents.title")}
              {selectedStakeholderForEdit.missingDocumentCount > 0 && (
                <span className="item-counter-alert">
                  {selectedStakeholderForEdit.missingDocumentCount}
                </span>
              )}
            </div>
            <DocumentsUploadFieldsetOverride
              requiredDocuments={requiredDocuments}
              disabled={isReadOnly}
              incompleteFields={incompleteFields}
            />
            <div className="section-title">
              {t("folder.stakeholders.fields.title")}

              {selectedStakeholderForEdit.missingRequiredFieldCount > 0 && (
                <span className="item-counter-alert">
                  {selectedStakeholderForEdit.missingRequiredFieldCount}
                </span>
              )}
            </div>

            <div className="grid grid-cols-4 gap-x-30 gap-y-20 mb-20 mt-10">
              <FormSelect
                control={form.control}
                name="title"
                required
                softRequired={incompleteFields.has("title")}
                label={t("form.addStakeholderFull.title")}
                options={genderOptions}
                className="w-full"
                disabled={isReadOnly}
              />
              <FormInput
                control={form.control}
                name="firstName"
                required
                label={t("form.addStakeholderFull.firstName")}
                className="w-full"
                disabled={isReadOnly}
              />
              <FormInput
                control={form.control}
                name="birthName"
                label={t("form.addStakeholderFull.birthName")}
                className="w-full"
                disabled={isReadOnly}
              />
              <FormInput
                control={form.control}
                name="lastName"
                required
                label={t("form.addStakeholderFull.lastName")}
                className="w-full"
                disabled={isReadOnly}
              />
            </div>

            <div className="grid grid-cols-4 gap-x-30 gap-y-20 mb-20 mt-10">
              <FormCheckboxMultiple
                control={form.control}
                name="roles"
                required
                label={t("form.addStakeholderFull.role")}
                options={stakeholderRoleOptions}
                disabled={isReadOnly}
              />
              <FormCombobox
                control={form.control}
                name="citizenshipCountryCode"
                required
                softRequired={incompleteFields.has("citizenshipCountryCode")}
                label={t("form.addStakeholderFull.citizenshipCountryCode")}
                options={countryOptions}
                isLoading={countryLoading}
                className="w-full"
                disabled={isReadOnly}
                isInModal={inModal}
              />
              <DatePicker
                name="birthDate"
                required
                softRequired={incompleteFields.has("birthDate")}
                label={t("form.addStakeholderFull.birthDate")}
                className="w-full"
                control={form.control}
                allowFutureDates={false}
                allowPastDates={true}
                maxDate={new Date()}
                disabled={isReadOnly}
              />
              <FormCombobox
                control={form.control}
                name="residenceCountryCode"
                required
                softRequired={incompleteFields.has("residenceCountryCode")}
                label={t("form.addStakeholderFull.residenceCountryCode")}
                options={countryWithRiskOptions}
                className="w-full"
                withBadge
                disabled={isReadOnly}
                isInModal={inModal}
              />
            </div>

            <div className="grid grid-cols-2 gap-x-30 gap-y-20 mb-20 mt-10">
              <FormCombobox
                control={form.control}
                name="birthCountryCode"
                required
                softRequired={incompleteFields.has("birthCountryCode")}
                label={t("form.addStakeholderFull.birthCountryCode")}
                options={countryOptions}
                isLoading={countryLoading}
                className="w-full"
                disabled={isReadOnly}
                isInModal={inModal}
              />
              <FormInput
                control={form.control}
                name="birthPlace"
                label={t("form.addStakeholderFull.birthPlace")}
                className="w-full"
                disabled={isReadOnly}
              />
            </div>

            <div
              className={cn(
                "flex flex-row",
                inModal &&
                  "justify-center gap-x-40 border-t border-border pt-10 sticky bottom-0 bg-gray-xlight",
                !inModal && "justify-end"
              )}
            >
              {inModal && (
                <Button
                  variant="link"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpen?.(false);
                  }}
                >
                  {t(isReadOnly ? "global.form.close" : "global.form.cancel")}
                </Button>
              )}
              {!isReadOnly && (
                <Button type="submit" loading={isLoading}>
                  {t("form.addStakeholderFull.submit")}
                </Button>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
